// ─────────────────────────────────────────────────────────────
// api/mockApi.js

// 这一层是阶段 2 接入真实后端的【唯一】改动点。
// 当后端就绪时:
//   1. 把每个函数体替换为 fetch('/api/...') 调用
//   2. 改成 async/await 返回真数据
//   3. 组件层零改动
// ─────────────────────────────────────────────────────────────

import { SYSTEMS, SOLID_PHASES } from '../data/systems';

export function rngFor(seed) {
  return (i) => { const x = Math.sin(seed + i * 9973) * 43758.5453; return x - Math.floor(x); };
}
export function generateDataPoints(systemId, temp) {
  const seed = systemId.charCodeAt(3) * 137 + Math.floor(temp * 7);
  const rng = rngFor(seed);
  const phases = SOLID_PHASES[systemId] || ["Salt A", "Salt B"];
  const pts = [];
  const nPts = 28 + Math.floor(rng(999) * 18);
  for (let i = 0; i < nPts; i++) {
    const angle = rng(i) * Math.PI / 2;
    const r = 0.05 + rng(i + 500) * 0.36;
    let w1 = Math.cos(angle) * r;
    let w2 = Math.sin(angle) * r;
    w1 += (temp - 25) * 0.0009 * (rng(i + 100) - 0.5);
    w2 += (temp - 25) * 0.0007 * (rng(i + 200) - 0.5);
    w1 = Math.max(0, Math.min(0.45, w1));
    w2 = Math.max(0, Math.min(0.45, w2));
    const ww = 1 - w1 - w2;
    if (ww < 0.3) continue;
    const phaseIdx = w1 > w2 * 1.5 ? 0 : w1 < w2 * 0.6 ? 1 : 2;
    pts.push({
      id: `${systemId}-${temp}-${i}`,
      w1: +w1.toFixed(4),
      w2: +w2.toFixed(4),
      ww: +ww.toFixed(4),
      temp,
      solidPhase: phases[phaseIdx % phases.length],
      source: ["Linke (1958)", "IUPAC-NIST", "Zdanovskii (1973)", "Lab-2024"][Math.floor(rng(i + 300) * 4)],
      uncertainty: +(0.001 + rng(i + 700) * 0.004).toFixed(4),
    });
  }
  return pts;
}
export function generatePredictionCurve(systemId, temp) {
  const seed = systemId.charCodeAt(3) * 31 + temp * 3;
  const rng = rngFor(seed);
  const curves = [];
  for (let seg = 0; seg < 3; seg++) {
    const pts = [];
    const n = 36;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      let w1, w2;
      if (seg === 0) { w1 = 0.02 + t * 0.32; w2 = 0.005 + 0.045 * Math.sin(t * Math.PI) + (temp - 25) * 0.0011; }
      else if (seg === 1) { w1 = 0.34 - t * 0.15; w2 = 0.045 + t * 0.28 + (temp - 25) * 0.0006; }
      else { w1 = 0.19 - t * 0.17; w2 = 0.325 - t * 0.05 + (temp - 25) * 0.0011; }
      w1 = Math.max(0, Math.min(0.5, w1 + rng(i + seg * 100) * 0.006));
      w2 = Math.max(0, Math.min(0.5, w2 + rng(i + seg * 200) * 0.006));
      pts.push({ w1, w2, ww: 1 - w1 - w2 });
    }
    curves.push(pts);
  }
  return curves;
}
export function generateInvariantPoints(systemId, temp) {
  const seed = systemId.charCodeAt(3) * 71 + temp;
  const rng = rngFor(seed);
  return [
    { w1: 0.32 + rng(1) * 0.02, w2: 0.04 + (temp - 25) * 0.0008, label: "E₁" },
    { w1: 0.19 + rng(2) * 0.01, w2: 0.32 + (temp - 25) * 0.0009, label: "E₂" },
  ];
}
export function generateModelDiagnostics(systemId) {
  const sys = SYSTEMS.find(s => s.id === systemId);
  if (!sys?.hasModel) return null;
  const seed = systemId.charCodeAt(3) * 53;
  const rng = rngFor(seed);
  const N = 120;
  const points = [];
  for (let i = 0; i < N; i++) {
    const exp = 0.02 + rng(i) * 0.45;
    const noise = (rng(i + 100) - 0.5) * (1 - sys.r2) * 0.18;
    points.push({ exp, pred: Math.max(0, exp + noise), absErr: Math.abs(noise) });
  }
  return { points, n: N, r2: sys.r2, mae: sys.mae, rmse: +(sys.mae * 1.42).toFixed(4) };
}
export function generateTempSeries(systemId, w1Fix = 0.15) {
  const sys = SYSTEMS.find(s => s.id === systemId);
  if (!sys) return [];
  const [tmin, tmax] = sys.tempRange;
  const pts = [];
  for (let t = tmin; t <= tmax; t += 5) {
    const exp = 0.04 + 0.0028 * (t - tmin) + 0.00002 * (t - tmin) * (t - tmin);
    const pred = exp + Math.sin(t * 0.3) * 0.003;
    pts.push({ t, exp, pred });
  }
  return pts;
}
